"use client";

import React, { useEffect, useState } from "react";
import { Download } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import LoadingIndicator from "@/components/LoadingIndicator/LoadingIndicator";
import { getCompanySubscriptionAgreements } from "../../api/getCompanySubscriptionAgreements";
import type { CompanySubscriptionAgreement } from "../../api/getCompanySubscriptionAgreements";

const formatSignedDate = (iso: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
};

const SubscriptionSettings: React.FC = () => {
  const [agreements, setAgreements] = useState<CompanySubscriptionAgreement[]>(
    [],
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getCompanySubscriptionAgreements()
      .then((res) => {
        if (!cancelled) setAgreements(res.data.agreements);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Subscription</CardTitle>
        <CardDescription>
          Your signed subscription agreements. Download a copy of any agreement
          on file for your records.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-8">
            <LoadingIndicator />
          </div>
        ) : agreements.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No signed agreements on file.
          </p>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-slate-700/50">
            {agreements.map((agreement) => (
              <li
                className="flex items-center justify-between gap-4 py-3"
                key={agreement.uuid}
              >
                <div className="min-w-0">
                  <p className="text-sm font-normal text-gray-900 dark:text-white">
                    {agreement.offeringName ?? agreement.subscriptionTypeCode}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Signed {formatSignedDate(agreement.signedAt)}
                    {agreement.signerName ? ` · ${agreement.signerName}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {agreement.source === "legacy_migration" && (
                    <Badge variant="secondary">Legacy</Badge>
                  )}
                  {agreement.downloadUrl ? (
                    <a
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 transition-colors"
                      href={agreement.downloadUrl}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      <Download className="h-4 w-4" />
                      Download
                    </a>
                  ) : (
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      Unavailable
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

export default SubscriptionSettings;
